import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { logger } from '../utils/logger';

// Información de producto y estilos
const PRODUCT_INFO = {
  quote: {
    name: 'Standard Auto Transport Quote',
    price: 0,
    description: 'Get your instant auto transport quote - completely free.',
    icon: (
      <svg
        width="48"
        height="48"
        fill="none"
        viewBox="0 0 24 24"
        className="mb-2 text-green-600"
      >
        <rect
          x="2"
          y="6"
          width="20"
          height="12"
          rx="2"
          fill="#16a34a"
          opacity="0.1"
        />
        <rect
          x="2"
          y="6"
          width="20"
          height="12"
          rx="2"
          stroke="#16a34a"
          strokeWidth="2"
        />
        <rect x="4" y="10" width="16" height="2" rx="1" fill="#16a34a" />
      </svg>
    ),
    textColor: 'text-green-700',
    bgColor: 'bg-green-600',
    bgHoverColor: 'hover:bg-green-700',
  },
  // NOTE: Carrier registration is FREE - removed carrier product info
};

function escape(str: string) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export interface PaymentSectionProps {
  email: string;
  formType: 'quote'; // NOTE: Carrier registration is FREE - no payment required
  recordId?: string;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PaymentSection: React.FC<PaymentSectionProps> = ({
  email,
  formType,
  recordId,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NOTE: This component should only be used for quotes
  // Carrier registration is FREE and doesn't require payment
  const { name, price, description, icon, textColor, bgColor, bgHoverColor } =
    PRODUCT_INFO.quote;

  const handlePayment = async () => {
    setError(null);

    // NOTE: This component only handles quote payments (transport services)
    // Carrier registration is FREE and handled separately

    // 1) Validar email antes de llamar al backend
    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Para quotes gratuitos, procesar directamente sin Stripe
    if (formType === 'quote' && price === 0) {
      setLoading(true);
      try {
        // Track free quote event
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'free_quote_request', {
            event_category: 'lead_generation',
            event_label: 'free_quote',
            value: 0,
            currency: 'USD',
          });
        }

        // Procesar quote gratuito directamente
        const res = await fetch('/.netlify/functions/process-free-quote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-notification-secret': process.env.REACT_APP_NOTIFICATION_SECRET || '',
          },
          body: JSON.stringify({ 
            email, 
            recordId: recordId || null 
          }),
        });

        if (!res.ok) {
          const text = await res.text();
          setError(text || 'Failed to process free quote.');
          return;
        }

        // Redirigir a página de éxito para quotes gratuitos
        window.location.href = '/success?type=quote';
      } catch {
        setError('Error processing free quote.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // For transport service payments (quotes that need payment)
    // This should use create-service-checkout, not create-checkout-session
    // Track payment start event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'payment_start', {
        event_category: 'ecommerce',
        event_label: 'transport_service',
        value: price,
        currency: 'USD',
        event_callback: () => {
          logger.log('payment_start event sent');
        },
      });
    }

    setLoading(true);
    try {
      // Use create-service-checkout for transport service payments
      const res = await fetch('/.netlify/functions/create-service-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-notification-secret':
            process.env.REACT_APP_NOTIFICATION_SECRET || '',
        },
        body: JSON.stringify({ 
          email, 
          quote_id: recordId || null 
        }),
      });

      // 2) Manejar errores HTTP explícitos
      if (!res.ok) {
        const text = await res.text();
        setError(text || 'Failed to initiate payment.');
        return;
      }

      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Failed to redirect to payment page.');
      }
    } catch {
      setError('Error connecting to payment gateway.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-14 px-6 pb-6 rounded-lg max-w-xl w-full relative bg-white shadow-lg">
      <button
        onClick={onClose}
        aria-label="Close modal"
        className="absolute top-4 right-4 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
      >
        <FontAwesomeIcon icon={faTimes} className="text-gray-800 text-lg" />
      </button>

      <div className="flex flex-col items-center text-center">
        {icon}
        <h2 className={`text-2xl font-bold ${textColor} mb-1`}>{name}</h2>
        <div className="text-3xl font-extrabold text-gray-900 mb-2">
          {formType === 'quote' && price === 0 ? (
            <span className="text-green-600">FREE</span>
          ) : (
            <>
              ${price}.00{' '}
              <span className="text-base font-medium text-gray-500">USD</span>
            </>
          )}
        </div>
        <p className="text-gray-600 text-sm mb-6">{description}</p>
        <button
          onClick={handlePayment}
          disabled={loading}
          className={`w-full ${bgColor} ${bgHoverColor} transition-colors text-white py-3 rounded-lg font-semibold text-lg shadow ${
            loading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="inline mr-2 animate-spin"
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="white"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="white"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                />
              </svg>
              Redirecting...
            </span>
          ) : (
            formType === 'quote' && price === 0 ? 'Get Free Quote' : `Pay $${price}`
          )}
        </button>
        <div className="mt-4 text-xs text-gray-400 text-center">
          Your payment is encrypted and processed securely via Stripe.
        </div>
        {error && (
          <div className="text-red-600 mt-4 text-center">{escape(error)}</div>
        )}
      </div>
    </div>
  );
};

export default PaymentSection;
